<template>
  <div class="page-center">
    <PasswordRecoveryForm v-if="mode === 'forgot-password'" @back="switchMode('login')" />
    <AuthCard v-else :title="mode === 'login' ? 'Вход' : mode === 'register' ? 'Регистрация' : mode === 'submitted' ? 'Заявка отправлена' : mode === 'recovery' ? 'Подтверждение регистрации' : 'Проверьте почту'"
      :subtitle="mode === 'recovery' ? 'Запрос нового письма' : mode === 'submitted' ? 'Регистрация на рассмотрении' : mode === 'pending' ? 'Подтверждение регистрации' : mode === 'login'
        ? 'Введите email и пароль'
        : 'Заполните данные, чтобы создать аккаунт'">
      <template v-if="mode === 'login'">
        <div class="field">
          <label for="login-email">Email</label>
          <InputText id="login-email" v-model.trim="loginForm.email" placeholder="example@mail.com"
            :invalid="hasError('email')" />
          <small v-if="hasError('email')" class="field-error">
            {{ getError('email') }}
          </small>
        </div>

        <div class="field">
          <label for="login-password">Пароль</label>
          <Password id="login-password" v-model="loginForm.password" placeholder="Пароль" :feedback="false" toggleMask
            :invalid="hasError('password')" />
          <small v-if="hasError('password')" class="field-error">
            {{ getError('password') }}
          </small>
        </div>

        <Button label="Войти" class="primary-btn" :loading="loading" :disabled="loading" @click="onLogin" />

        <Button label="Забыли пароль?" text :disabled="loading" @click="switchMode('forgot-password')" />

        <div class="bottom-link">
          <span>Нет аккаунта?</span>
          <a href="#" @click.prevent="switchMode('register')">Зарегистрироваться</a>
        </div>
      </template>

      <template v-else-if="mode === 'register'">
        <div class="field">
          <label for="reg-email">Email</label>
          <InputText id="reg-email" v-model.trim="regForm.email" placeholder="example@mail.com"
            :invalid="hasError('email')" />
          <small v-if="hasError('email')" class="field-error">
            {{ getError('email') }}
          </small>
        </div>

        <div class="field">
          <label for="reg-fullname">Полное имя</label>
          <InputText id="reg-fullname" v-model.trim="regForm.fullName" placeholder="Иванов Иван"
            :invalid="hasError('fullName')" />
          <small v-if="hasError('fullName')" class="field-error">
            {{ getError('fullName') }}
          </small>
        </div>

        <div class="field">
          <label for="reg-company">Название компании</label>
          <InputText id="reg-company" v-model.trim="regForm.companyName" placeholder="ТОО Ромашка"
            :invalid="hasError('companyName')" />
          <small v-if="hasError('companyName')" class="field-error">
            {{ getError('companyName') }}
          </small>
        </div>

        <div class="field">
          <label for="reg-bin">БИН</label>
          <InputText id="reg-bin" v-model.trim="regForm.bin" placeholder="123456789012" :invalid="hasError('bin')"
            maxlength="12" @input="validateBin" />
          <small v-if="hasError('bin')" class="field-error">
            {{ getError('bin') }}
          </small>
        </div>

        <div class="field">
          <label for="reg-password">Пароль</label>
          <Password id="reg-password" v-model="regForm.password" placeholder="Пароль" :feedback="true"
            :toggle-mask="true" :invalid="hasError('password')" :prompt-label="'Введите пароль'" :weak-label="'Слабый'"
            :medium-label="'Средний'" :strong-label="'Сильный'" />
          <small v-if="hasError('password')" class="field-error">
            {{ getError('password') }}
          </small>

          <div class="password-requirements" v-if="regForm.password">
            <div class="requirement" :class="{ met: hasMinLength }">
              <i :class="hasMinLength ? 'pi pi-check-circle' : 'pi pi-times-circle'" />
              <span>Минимум 6 символов</span>
            </div>
            <div class="requirement" :class="{ met: hasUpperCase }">
              <i :class="hasUpperCase ? 'pi pi-check-circle' : 'pi pi-times-circle'" />
              <span>Заглавная буква</span>
            </div>
            <div class="requirement" :class="{ met: hasLowerCase }">
              <i :class="hasLowerCase ? 'pi pi-check-circle' : 'pi pi-times-circle'" />
              <span>Строчная буква</span>
            </div>
            <div class="requirement" :class="{ met: hasDigit }">
              <i :class="hasDigit ? 'pi pi-check-circle' : 'pi pi-times-circle'" />
              <span>Цифра</span>
            </div>
          </div>
        </div>

        <div class="field">
          <label for="reg-confirm-password">Подтвердить пароль</label>
          <Password id="reg-confirm-password" v-model="regForm.confirmPassword" placeholder="Повторите пароль"
            :feedback="false" toggleMask :invalid="hasError('confirmPassword')" />
          <small v-if="hasError('confirmPassword')" class="field-error">
            {{ getError('confirmPassword') }}
          </small>
        </div>

        <div class="actions">
          <Button label="Зарегистрироваться" class="primary-btn" :loading="loading"
            :disabled="loading || !isPasswordValid" @click="onRegister" />

          <Button label="Отмена" severity="secondary" outlined :disabled="loading" @click="switchMode('login')" />
        </div>
      </template>

      <template v-else-if="mode === 'submitted'">
        <p class="pending-message">Заявка отправлена на рассмотрение.</p>
        <p class="pending-message">Мы проверим предоставленную информацию и сообщим о решении на указанную электронную почту.</p>
        <p class="pending-email">{{ pendingEmail }}</p>
        <Button label="Вернуться ко входу" severity="secondary" outlined @click="switchMode('login')" />
      </template>

      <template v-else-if="mode === 'recovery'">
        <p class="pending-message">Ссылка недействительна или срок её действия истёк. Укажите email, чтобы запросить новое письмо.</p>
        <div class="field">
          <label for="recovery-email">Email</label>
          <InputText id="recovery-email" v-model.trim="pendingEmail" placeholder="example@mail.com" />
        </div>
        <Button :label="resendSeconds > 0 ? `Повторить через ${resendSeconds} с` : 'Запросить новое письмо'"
          :loading="loading" :disabled="loading || !pendingEmail || resendSeconds > 0" @click="onResend" />
        <Button label="Вернуться ко входу" severity="secondary" outlined @click="switchMode('login')" />
      </template>

      <template v-else>
        <p class="pending-message">
          Если адрес указан верно, мы отправили письмо для подтверждения регистрации.
        </p>
        <p class="pending-email">{{ pendingEmail }}</p>
        <div class="actions">
          <Button :label="resendSeconds > 0 ? `Отправить повторно через ${resendSeconds} с` : 'Отправить письмо повторно'"
            class="primary-btn" :loading="loading" :disabled="loading || resendSeconds > 0" @click="onResend" />
          <Button label="Изменить email" severity="secondary" outlined :disabled="loading"
            @click="switchMode('register')" />
          <Button label="Войти" severity="secondary" text :disabled="loading" @click="switchMode('login')" />
        </div>
      </template>
    </AuthCard>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, onUnmounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'

import PasswordRecoveryForm from '@/modules/auth/components/PasswordRecoveryForm.vue'
import AuthCard from '@/shared/ui/AuthCard.vue'
import { useAppToast } from '@/shared/lib/useAppToast'

import InputText from 'primevue/inputtext'
import Password from 'primevue/password'
import Button from 'primevue/button'

import { login, register, resendConfirmation } from '@/modules/auth/api/auth.service'
import { getMe } from '@/modules/auth/api/user.service'
import { useAuthStore } from '@/modules/auth/store/auth.store'
import { usePermissionsStore } from '@/modules/auth/store/permissions.store'
import { publishAuthChanged } from '@/modules/auth/services/auth-channel.service'
import { useCartStore } from '@/modules/cart/store/cart.store'

const authStore = useAuthStore()
const cartStore = useCartStore()
const permissionsStore = usePermissionsStore()

const router = useRouter()
const route = useRoute()
const toast = useAppToast()

const mode = ref('login')
const loading = ref(false)
const fieldErrors = ref({})
const pendingEmail = ref('')
const resendSeconds = ref(0)
let cooldownTimer = null

const loginForm = reactive({
  email: '',
  password: ''
})

const regForm = reactive({
  email: '',
  password: '',
  confirmPassword: '',
  fullName: '',
  companyName: '',
  bin: ''
})

const hasMinLength = computed(() => regForm.password.length >= 6)
const hasUpperCase = computed(() => /[A-Z]/.test(regForm.password))
const hasLowerCase = computed(() => /[a-z]/.test(regForm.password))
const hasDigit = computed(() => /\d/.test(regForm.password))

const isPasswordValid = computed(() => {
  return hasMinLength.value && hasUpperCase.value && hasLowerCase.value && hasDigit.value
})

onMounted(() => {
  if (route.query.confirmed === 'true') {
    toast.success('Регистрация завершена. Теперь вы можете войти.', 'Email подтвержден')
    mode.value = 'login'
    router.replace('/auth')
  }

  if (route.query.confirmed === 'false') {
    mode.value = 'recovery'
    toast.error('Ссылка недействительна или срок её действия истёк')
    router.replace('/auth')
  }
})

onUnmounted(() => {
  if (cooldownTimer) window.clearInterval(cooldownTimer)
})

function startCooldown(seconds = 120) {
  if (cooldownTimer) window.clearInterval(cooldownTimer)
  resendSeconds.value = Math.max(0, Number(seconds) || 0)
  if (resendSeconds.value === 0) return
  cooldownTimer = window.setInterval(() => {
    resendSeconds.value -= 1
    if (resendSeconds.value <= 0) {
      window.clearInterval(cooldownTimer)
      cooldownTimer = null
    }
  }, 1000)
}

function enterPending(email, cooldownSeconds = 120) {
  pendingEmail.value = email
  mode.value = 'pending'
  clearFieldErrors()
  startCooldown(cooldownSeconds)
}

function clearFieldErrors() {
  fieldErrors.value = {}
}

function setFieldError(field, message) {
  fieldErrors.value = {
    ...fieldErrors.value,
    [field]: message
  }
}

function hasError(field) {
  return Boolean(getError(field))
}

function getError(field) {
  const errors = fieldErrors.value || {}

  return (
    errors[field] ||
    errors[field?.charAt(0).toUpperCase() + field?.slice(1)] ||
    ''
  )
}

function resetLoginForm() {
  loginForm.email = ''
  loginForm.password = ''
}

function resetRegisterForm() {
  regForm.email = ''
  regForm.password = ''
  regForm.confirmPassword = ''
  regForm.fullName = ''
  regForm.companyName = ''
  regForm.bin = ''
}

function switchMode(next) {
  mode.value = next
  clearFieldErrors()

  if (next === 'login') {
    resetLoginForm()
  }

  if (next === 'register') {
    resetRegisterForm()
  }
}

function validateLoginForm() {
  clearFieldErrors()

  if (!loginForm.email) {
    setFieldError('email', 'Введите email')
  } else if (!loginForm.email.includes('@')) {
    setFieldError('email', 'Введите корректный email')
  }

  if (!loginForm.password) {
    setFieldError('password', 'Введите пароль')
  }

  return Object.keys(fieldErrors.value).length === 0
}

function validateRegisterForm() {
  clearFieldErrors()

  if (!regForm.email) {
    setFieldError('email', 'Введите email')
  } else if (!regForm.email.includes('@')) {
    setFieldError('email', 'Введите корректный email')
  }

  if (!regForm.fullName) {
    setFieldError('fullName', 'Введите полное имя')
  }

  if (!regForm.password) {
    setFieldError('password', 'Введите пароль')
  } else if (!isPasswordValid.value) {
    setFieldError('password', 'Пароль должен содержать минимум 6 символов, заглавную и строчную букву, цифру')
  }

  if (!regForm.confirmPassword) {
    setFieldError('confirmPassword', 'Подтвердите пароль')
  } else if (regForm.password !== regForm.confirmPassword) {
    setFieldError('confirmPassword', 'Пароли не совпадают')
  }

  if (!regForm.companyName) {
    setFieldError('companyName', 'Укажите название компании')
  }

  return Object.keys(fieldErrors.value).length === 0
}

async function onLogin() {
  if (!validateLoginForm()) {
    toast.error('Проверьте введённые данные')
    return
  }

  loading.value = true

  try {
    await login({
      email: loginForm.email,
      password: loginForm.password,
      rememberMe: true
    })
    publishAuthChanged()

    const me = await getMe()
    authStore.setUser(me.data)
    permissionsStore.clearPermissions()
    await permissionsStore.fetchPermissions(true)

    const currentUserId = authStore.user?.id ?? authStore.user?.Id ?? null

    if (currentUserId) {
      await cartStore.mergeGuestCartToUserCart(currentUserId)
      await cartStore.loadCartByUserId(currentUserId)
    }

    toast.success('Вы вошли в систему')
    await router.push('/cabinet')
  } catch (error) {
    if (error.code === 'EMAIL_CONFIRMATION_REQUIRED') {
      enterPending(loginForm.email, 0)
      toast.info('Для продолжения подтвердите адрес электронной почты.')
      return
    }
    fieldErrors.value = error.fieldErrors || {}
    toast.error(error.message || 'Ошибка входа')
  } finally {
    loading.value = false
  }
}

async function onRegister() {
  if (!validateRegisterForm()) {
    toast.error('Проверьте введённые данные')
    return
  }

  loading.value = true

  try {
    const payload = {
      email: regForm.email,
      password: regForm.password,
      clientType: 1,
      fullName: regForm.fullName,
      companyName: regForm.companyName,
      bin: regForm.bin || null
    }

    await register(payload)
    pendingEmail.value = regForm.email
    mode.value = 'submitted'
    clearFieldErrors()
    startCooldown(0)
    regForm.password = ''
    regForm.confirmPassword = ''
    toast.success('Заявка отправлена на рассмотрение.')
  } catch (error) {
    if (error.status === 400 || error.status === 409) {
      fieldErrors.value = error.fieldErrors || {}
      toast.error(getError('email') || 'Проверьте введённые данные.')
    } else {
      clearFieldErrors()
      toast.error(error.status === 0
        ? 'Не удалось подключиться к серверу. Проверьте соединение и попробуйте ещё раз.'
        : 'Не удалось завершить регистрацию. Попробуйте позже.')
    }
  } finally {
    loading.value = false
  }
}

async function onResend() {
  if (!['pending', 'recovery'].includes(mode.value) || !pendingEmail.value || resendSeconds.value > 0) return
  loading.value = true
  try {
    const response = await resendConfirmation(pendingEmail.value)
    startCooldown(response.data?.resendAfterSeconds)
    toast.success('Если адрес существует и требует подтверждения, письмо будет отправлено.')
  } catch (error) {
    if (error.status === 429) {
      startCooldown(error.retryAfterSeconds || 120)
      toast.error('Слишком много запросов. Попробуйте позже.')
    } else {
      toast.error('Не удалось обработать запрос. Попробуйте позже.')
    }
  } finally {
    loading.value = false
  }
}

function validateBin(event) {
  let value = event.target.value.replace(/\D/g, '')

  if (value.length > 12) {
    value = value.slice(0, 12)
  }

  regForm.bin = value
}


if (!regForm.bin) {
  setFieldError('bin', 'Введите БИН')
} else if (regForm.bin.length !== 12) {
  setFieldError('bin', 'БИН должен содержать 12 цифр')
} else if (!/^\d+$/.test(regForm.bin)) {
  setFieldError('bin', 'БИН должен содержать только цифры')
}
</script>

<style scoped>
.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

label {
  font-size: 13px;
  color: var(--muted);
}

.field-error {
  font-size: 12px;
  color: #dc2626;
}

.actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

:deep(.p-inputtext),
:deep(.p-password-input) {
  width: 100%;
  border-color: var(--border);
  background: var(--input-bg);
  color: #1f2a2e;
}

:deep(.p-inputtext::placeholder),
:deep(.p-password-input::placeholder) {
  color: #8a9aa0;
}

:deep(.p-dropdown) {
  width: 100%;
  background: var(--input-bg);
  border-color: var(--border);
  color: #1f2a2e;
}

.bottom-link {
  margin-top: 6px;
  font-size: 14px;
  color: var(--muted);
  display: flex;
  justify-content: center;
  gap: 6px;
}

.bottom-link a {
  color: var(--primary);
  text-decoration: none;
  font-weight: 600;
}

.bottom-link a:hover {
  text-decoration: underline;
}

.password-requirements {
  margin-top: 8px;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.pending-message,
.pending-email {
  text-align: center;
  color: var(--muted);
}

.pending-email {
  font-weight: 600;
  overflow-wrap: anywhere;
}

.requirement {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #6c757d;
  margin-bottom: 4px;
}

.requirement:last-child {
  margin-bottom: 0;
}

.requirement i {
  font-size: 12px;
}

.requirement.met {
  color: #28a745;
}

.requirement.met i {
  color: #28a745;
}

.requirement i.pi-times-circle {
  color: #dc3545;
}
</style>
